import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Star,
  Home as HomeIcon,
  ShieldCheck,
  Clock,
  ClipboardCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import StickyQuoteButton from "../components/StickyQuoteButton";
import { brand, services, IMAGES } from "../mock";

/**
 * Dedicated landing page for Airbnb Turnover Cleaning.
 * SEO-targeted at Brisbane short-stay hosts.
 */
const HERO_PHOTO =
  "https://customer-assets.emergentagent.com/job_sterling-landing/artifacts/ofg66wrm_White%20Bedroom.png";

const AirbnbTurnoverPage = () => {
  const service = services.find((s) => s.id === "airbnb-turnover");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!service) return null;

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Airbnb Turnover Cleaning Brisbane | The Sterling Touch"
        description="Professional, reliable Airbnb turnover cleaning across Brisbane. Beds made, bathrooms sanitised, linen restocked, damage flagged. 5★ ready, every turnover."
        path="/services/airbnb-turnover-cleaning"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Airbnb Turnover Cleaning", path: "/services/airbnb-turnover-cleaning" },
        ]}
      />
      <StickyQuoteButton />
      <Header />

      {/* HERO */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_PHOTO}
            alt="Airbnb-ready bedroom — Brisbane"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#fdfbf7]/80 via-[#fdfbf7]/30 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur border border-[#e8d9cc] text-xs uppercase tracking-[0.2em] text-[#b8866e]">
              <Sparkles size={14} className="text-sterling-dark" />
              Airbnb Turnover Cleaning · Brisbane
            </div>

            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-ink mt-6 leading-[1.05] text-balance">
              We clean,{" "}
              <span className="italic text-[#daae9c]">so you can host.</span>
            </h1>

            <p className="mt-6 text-lg text-ink/80 leading-relaxed max-w-xl">
              Reliable turnover cleans that keep your Brisbane property
              guest-ready, protect your investment, and help you earn{" "}
              <strong>5★ reviews</strong> — every stay.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#turnover-quote">
                <Button className="rounded-full bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors px-7 h-12 group">
                  Book a Turnover Clean
                  <ArrowRight
                    size={16}
                    className="ml-2 group-hover:translate-x-1 transition-transform"
                  />
                </Button>
              </a>
              <a href="#whats-included">
                <Button
                  variant="outline"
                  className="rounded-full bg-white/95 border-[#daae9c] text-ink hover:bg-[#daae9c] hover:text-ink px-7 h-12 transition-colors"
                >
                  What&rsquo;s included
                </Button>
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink/70">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-sterling-dark" />
                Same-day turnaround
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-sterling-dark" />
                Fully insured
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-sterling-dark" />
                Police checked
              </span>
            </div>
          </div>

          {/* Right column intentionally blank on desktop — lets the bedroom
              photo breathe behind the headline. */}
          <div className="hidden lg:block lg:col-span-5" />
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-12 bg-white border-y border-[#ead8c9]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: Star,
              title: "5★ Ready",
              desc: "Every turnover prepared to guest-review standard.",
            },
            {
              icon: Clock,
              title: "Same-Day",
              desc: "Tight checkout-to-check-in windows handled with care.",
            },
            {
              icon: ShieldCheck,
              title: "Damage Flagging",
              desc: "Issues reported early so you can protect your investment.",
            },
            {
              icon: ClipboardCheck,
              title: "Quality Checked",
              desc: "Every clean inspected before guests arrive.",
            },
          ].map((t) => (
            <div key={t.title} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#faf3eb] flex items-center justify-center shrink-0">
                <t.icon size={18} className="text-sterling-dark" />
              </div>
              <div>
                <div className="font-serif-display text-lg text-ink leading-tight">
                  {t.title}
                </div>
                <p className="text-sm text-ink/65 mt-1 leading-relaxed">
                  {t.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section
        id="whats-included"
        className="py-20 lg:py-24 bg-[#faf3eb]"
        data-testid="airbnb-included"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#daae9c]">
              <span className="w-6 h-px bg-sterling-dark" />
              What&rsquo;s Included
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink mt-4 leading-[1.05]">
              Professional care.{" "}
              <span className="italic text-[#daae9c]">Every turnover.</span>
            </h2>
            <p className="mt-5 text-ink/70 leading-relaxed">
              A meticulous, repeatable standard between every guest — designed
              for short-stay hosts who care about their reviews and their
              property.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {service.included.map((item) => (
              <div
                key={item.title}
                className="p-6 bg-white rounded-2xl border border-[#ead8c9] hover:border-sterling-dark transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-[#faf3eb] flex items-center justify-center mb-4">
                  <CheckCircle2 size={16} className="text-sterling-dark" />
                </div>
                <div className="font-serif-display text-lg text-ink leading-tight">
                  {item.title}
                </div>
                <p className="text-sm text-ink/65 mt-2 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROPERTY SIZES */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#daae9c]">
              Turnover Packages
            </div>
            <h2 className="font-serif-display text-4xl text-ink mt-3 leading-tight">
              Built for{" "}
              <span className="italic text-[#daae9c]">every property.</span>
            </h2>
            <p className="mt-4 text-ink/70 leading-relaxed">
              From inner-city apartments to multi-level homes — we tailor each
              turnover to the property, the guest count and the brand standard
              you want to maintain.
            </p>
            <p className="mt-4 text-sm text-ink/55">
              Pricing is based on property size, condition and services
              required. Linen service available as an add-on.
            </p>
          </div>

          <div className="lg:col-span-7 space-y-3">
            {service.packages.map((p) => (
              <div
                key={p.name}
                className="flex items-center justify-between gap-4 p-5 bg-[#faf3eb] border border-[#ead8c9] rounded-2xl"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white border border-[#ead8c9] flex items-center justify-center shrink-0">
                    <HomeIcon size={18} className="text-sterling-dark" />
                  </div>
                  <div>
                    <div className="font-serif-display text-xl text-ink leading-tight">
                      {p.name}
                    </div>
                    <div className="text-sm text-ink/60 mt-0.5">{p.desc}</div>
                  </div>
                </div>
                <a
                  href="#turnover-quote"
                  className="text-xs uppercase tracking-[0.2em] text-[#b8866e] hover:text-ink transition-colors inline-flex items-center gap-1 shrink-0"
                >
                  Quote me <ArrowRight size={12} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-20 bg-[#faf3eb] border-y border-[#ead8c9]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-[#daae9c]">
              Who It&rsquo;s For
            </div>
            <h2 className="font-serif-display text-4xl text-ink mt-3 leading-tight">
              Made for{" "}
              <span className="italic text-[#daae9c]">Brisbane hosts.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
            {service.perfectFor.map((p) => (
              <div
                key={p}
                className="p-5 bg-white border border-[#ead8c9] rounded-2xl flex items-start gap-3"
              >
                <CheckCircle2
                  size={18}
                  className="text-sterling-dark shrink-0 mt-0.5"
                />
                <span className="text-sm text-ink/85 leading-relaxed">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        id="turnover-quote"
        className="py-20 lg:py-24 bg-white"
        data-testid="airbnb-cta"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#daae9c]">
            Ready when you are
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-ink mt-3 leading-tight">
            A clean space.{" "}
            <span className="italic text-[#daae9c]">A great stay.</span>
          </h2>
          <p className="mt-5 text-ink/70 leading-relaxed">
            Tell us about your Brisbane property — number of bedrooms,
            bathrooms, frequency of bookings and any extras (linen, mid-stay
            refresh, outdoor tidy). We&rsquo;ll respond within 24 hours with a
            tailored turnover quote.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/quote?service=airbnb-turnover">
              <Button className="rounded-full bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors px-7 h-12 group">
                Request a Turnover Quote
                <ArrowRight
                  size={16}
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                />
              </Button>
            </Link>
            <a href={`tel:${brand.phone.replace(/\s/g, "")}`}>
              <Button
                variant="outline"
                className="rounded-full bg-white border-[#daae9c] text-ink hover:bg-[#daae9c] hover:text-ink px-7 h-12 transition-colors"
              >
                Call {brand.phone}
              </Button>
            </a>
          </div>
          <p className="mt-6 text-xs text-ink/50">
            Servicing Brisbane&rsquo;s inner west, north and prestige riverside
            suburbs — Teneriffe, New Farm, Hamilton, Ascot, Paddington, Bulimba
            and more.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AirbnbTurnoverPage;
